// src/utils/openHouseOpportunityHelpers.js
import { activeUsers } from "./roles";

// Lifecycle of an "up for grabs" opportunity and of each agent request on it.
export const OPP_STATUS = { OPEN: "Open", FILLED: "Filled", CANCELLED: "Cancelled" };
export const REQUEST_STATUS = { PENDING: "Pending", APPROVED: "Approved", DECLINED: "Declined" };

// Build one agent's request. `id` is supplied by the caller (e.g. crypto.randomUUID())
// so this stays pure and testable.
export function buildRequest({
  id,
  agentId,
  agentName,
  agentEmail,
  proposedDate,
  proposedStartTime,
  proposedEndTime,
  note,
}) {
  return {
    id,
    agentId,
    agentName: agentName || "",
    agentEmail: agentEmail || "",
    proposedDate: proposedDate || "",
    proposedStartTime: proposedStartTime || "",
    proposedEndTime: proposedEndTime || "",
    note: note || "",
    status: REQUEST_STATUS.PENDING,
  };
}

// Add a request, replacing the same agent's existing *pending* request so an
// agent can revise their proposal without stacking duplicates. Declined history
// is preserved.
export function upsertRequest(requests, request) {
  const others = (requests || []).filter(
    (r) => !(r.agentId === request.agentId && r.status === REQUEST_STATUS.PENDING),
  );
  return [...others, request];
}

// Mark the winning request Approved and decline the rest of the pending ones.
export function applyWinner(requests, winnerRequestId) {
  return (requests || []).map((r) => {
    if (r.id === winnerRequestId) return { ...r, status: REQUEST_STATUS.APPROVED };
    if (r.status === REQUEST_STATUS.PENDING) return { ...r, status: REQUEST_STATUS.DECLINED };
    return r;
  });
}

// Decline every pending request (used by Decline-All and by cancellation).
export function declineAll(requests) {
  return (requests || []).map((r) =>
    r.status === REQUEST_STATUS.PENDING ? { ...r, status: REQUEST_STATUS.DECLINED } : r,
  );
}

export function pendingRequests(requests) {
  return (requests || []).filter((r) => r.status === REQUEST_STATUS.PENDING);
}

// Who should approve a claimed open house on this listing? Primary: the listing
// agent's assigned Listing Coordinator. Fallback: active Admins + Supervisors
// (shared queue) when no LC is set or that LC is gone/inactive.
export function resolveOpportunityApprovers(deal, users) {
  const active = activeUsers(users);
  const listingAgent = active.find((u) => u.id === deal?.agentData);
  const lcId = listingAgent?.listingCoordinatorId;
  if (lcId) {
    const lc = active.find((u) => u.id === lcId);
    if (lc) return [lc.id];
  }
  return active
    .filter((u) => u.role === "Admin" || u.role === "Supervisor")
    .map((u) => u.id);
}

// May this user act on an opportunity's approval queue? Admin/Supervisor always;
// otherwise only the LC the opportunity was routed to.
export function canApprove(opportunity, user) {
  if (!user) return false;
  if (user.role === "Admin" || user.role === "Supervisor") return true;
  return !!opportunity?.approverLcId && opportunity.approverLcId === user.id;
}
